/**
 * FIRST NIGHT flow logic — pure, DOM-free, unit-testable. The S1–S11 journey
 * (S11 removed — the flow ends mid-loop into /feed) as data: the step order,
 * the resume-step machine (which S to re-enter from a given OnboardingState),
 * catalogue→grid mapping, the deliberate 5→0→5 economy math, and the S9
 * gamertag inline-check debounce. The orchestrator (routes/FirstNight.tsx)
 * renders over these; the ceremonies are the juice engine's job, not this file.
 *
 * NO streak/flame anything (MOMENTS supersedes FIRST BLOOD S9 — see JUICE
 * scripts.ts). NO server events emitted anywhere in the client.
 */
import type {
  AvatarOption,
  BadgeOption,
  GamertagCheck,
  IdentityCatalogue,
  OnboardingState,
} from "@matchday/contracts";

// ── The journey ─────────────────────────────────────────────────────────────
// S5 (the locker hub) recurs at three states: v1 (after S4 coins), v2 (spend,
// after S7 confidence) and v3 (complete, after S9 identity). "done" hands off
// to /feed — S11 (the feed) is the other builders' surface, not ours.
export type FirstNightStep =
  | "s1" // Games Night home (code already entered on Landing)
  | "s2" // Pick Your Badge (10 shields + lucky chest)
  | "s3" // Got the game?
  | "s4" // +5 Coins ceremony (claimGrant starter_coins)
  | "s5v1" // Locker Room v1 — coins + confidence tile live
  | "s6" // Confidence rules
  | "s7" // +10 Confidence ceremony (claimGrant confidence_intro)
  | "s5v2" // Locker Room v2 — SPEND COINS
  | "s8" // Rewards Store
  | "s9" // Avatar + Gamertag (claimIdentity — coins 5→0, READY)
  | "s5v3" // Locker Room v3 — complete, HOST YOUR FIRST MATCH
  | "s10" // Host your first match (+5 host bonus)
  | "done"; // → /feed

/** Linear order — drives the orchestrator's `next` and any progress display. */
export const FIRST_NIGHT_STEPS: readonly FirstNightStep[] = [
  "s1",
  "s2",
  "s3",
  "s4",
  "s5v1",
  "s6",
  "s7",
  "s5v2",
  "s8",
  "s9",
  "s5v3",
  "s10",
  "done",
];

/** The step after `step` in the linear journey (`done` is terminal). */
export function nextStep(step: FirstNightStep): FirstNightStep {
  const i = FIRST_NIGHT_STEPS.indexOf(step);
  if (i < 0 || i >= FIRST_NIGHT_STEPS.length - 1) return "done";
  return FIRST_NIGHT_STEPS[i + 1] as FirstNightStep;
}

/** Steps that are real, resumable positions. "done" is terminal, not a step. */
const RESUMABLE = new Set<string>(FIRST_NIGHT_STEPS.filter((s) => s !== "done"));

/**
 * The resume-step machine — where a returning player re-enters.
 *
 * ── COMPLETION IS HOSTING, NOT DRESSING (BK, 28 Jul) ────────────────────────
 * This used to return "done" for anyone `ready`. `ready` is set at S9 (avatar +
 * gamertag); the steps after it are s5v3 and **s10, "Host your first match"**.
 * So a player got their gamertag, felt finished, closed the app — and came back
 * to the feed having never met the host tutorial. The single beat the flow
 * exists to deliver was skipped, at the single most likely place to bail,
 * because S9 is where the reward lands.
 *
 * Completion is now `onboarding_completed_at`, stamped at s10. `ready` keeps
 * its own meaning (identity complete) because other surfaces read it that way.
 *
 * ── PREFER THE RECORDED STEP ────────────────────────────────────────────────
 * `onboarding_step` is written on every transition, so a returning player
 * lands exactly where they left. The balance inference below is the FALLBACK,
 * for rows written before that column existed and for anyone whose step is
 * somehow unrecognised. It is deliberately coarse — five anchors for twelve
 * steps — and grants are idempotent server-side, so landing one beat early is
 * always safe. Never one beat LATE: that would skip a ceremony.
 */
export function resumeStep(state: OnboardingState): FirstNightStep {
  if (state.onboarding_completed_at !== null) return "done";

  // The recorded step wins — but only if it is a step we still ship. A value
  // from a renamed or removed step must fall through to inference rather than
  // route someone at a screen that no longer exists.
  const recorded = state.onboarding_step;
  if (recorded !== null && RESUMABLE.has(recorded)) return recorded as FirstNightStep;

  // ── Fallback: infer from balances ─────────────────────────────────────────
  // `ready` FIRST. It no longer ends the flow, but it is the strongest single
  // fact available: identity is complete, so the only thing left is the host
  // beat. Checked before the badge branch so that an odd-looking row — a badge
  // cleared by a migration, a partial write — can never send a player who has
  // already built an identity back to step one to do it again.
  if (state.ready) return "s5v3";

  // Defaults (starter 5 / intro 10) are positive, so coins>0 ⟺ starter granted
  // and confidence>0 ⟺ intro granted.
  if (state.badge_id === null) return "s1";
  if (state.coins === 0 && state.confidence === 0) return "s3"; // badge set, starter not yet granted
  if (state.confidence === 0) return "s5v1"; // starter granted, confidence not yet
  return "s5v2"; // confidence granted, identity not done → spend
}

/**
 * Landing's decision: an UNFINISHED player enters onboarding; a finished one
 * the feed.
 *
 * Reads completion, not `ready` — same reason as resumeStep. A player with a
 * gamertag but no hosted match is not finished, and sending them to the feed is
 * how the host tutorial got skipped.
 */
export function landingDestination(state: OnboardingState): "/first-night" | "/feed" {
  return state.onboarding_completed_at !== null ? "/feed" : "/first-night";
}

/**
 * Should this player be pulled back into onboarding from wherever they are?
 *
 * The gate behind the entry-point bounce. Kept as its own predicate so the one
 * definition of "not finished" is shared by Landing, Auth and the chrome, and
 * cannot drift between them.
 */
export function shouldResumeOnboarding(state: OnboardingState): boolean {
  return state.onboarding_completed_at === null;
}

// ── Catalogue → grid mapping ─────────────────────────────────────────────────

/** S2: the 10 shields shown in the grid, plus the rare pool behind the chest. */
export interface BadgeGrid {
  shields: BadgeOption[]; // lucky === false — the 4-4-3 wall
  luckyPool: BadgeOption[]; // lucky === true — the 11th-slot chest reveals one
}

export function badgeGrid(catalogue: IdentityCatalogue): BadgeGrid {
  return {
    shields: catalogue.badges.filter((b) => !b.lucky),
    luckyPool: catalogue.badges.filter((b) => b.lucky),
  };
}

/** S9 avatar tile — scarcity resolved for the gold chip + sold-out gating. */
export interface AvatarCell {
  id: string;
  priceCoins: number;
  /** null for the standard set; live scarcity for the limited editions. */
  limited: { total: number; claimed: number; left: number } | null;
  soldOut: boolean;
  /** "1/10 LEFT" for limited, null otherwise. */
  scarcityLabel: string | null;
}

export function avatarCell(a: AvatarOption): AvatarCell {
  if (a.limited === null) {
    return { id: a.id, priceCoins: a.price_coins, limited: null, soldOut: false, scarcityLabel: null };
  }
  const left = Math.max(0, a.limited.total - a.limited.claimed);
  return {
    id: a.id,
    priceCoins: a.price_coins,
    limited: { ...a.limited, left },
    soldOut: left === 0,
    scarcityLabel: `${left}/${a.limited.total} LEFT`,
  };
}

/** S8/S9: the active avatars as grid cells (inactive tiles never render live). */
export function avatarGrid(catalogue: IdentityCatalogue): AvatarCell[] {
  return catalogue.avatars.filter((a) => a.active).map(avatarCell);
}

/**
 * KIT CHECK Rule 1 slot label — the short token a self-announcing AWAITING slot
 * shows in place of invented art. "badge_b4_std" → "B4", "av_g7_ltd" → "G7".
 */
export function slotLabel(id: string): string {
  const m = /_([a-z])(\d+)_/i.exec(id);
  if (m) return `${m[1]!.toUpperCase()}${m[2]}`;
  return id.toUpperCase();
}

// ── The economy (deliberate 5 → 0 → 5) ───────────────────────────────────────

/** The three coin-balance beats a new player lives: grant → spend → refill. */
export function economySequence(starter: number, avatarPrice: number, hostBonus: number): number[] {
  const afterSpend = Math.max(0, starter - avatarPrice);
  return [starter, afterSpend, afterSpend + hostBonus];
}

/**
 * The design invariant: the one live avatar costs EXACTLY the starter grant, so
 * the balance visibly hits 0 before hosting refills it. True ⟺ the middle beat
 * of economySequence is 0.
 */
export function economyBalances(catalogue: IdentityCatalogue, avatarPrice: number, hostBonus: number) {
  const seq = economySequence(catalogue.starter_grant_coins, avatarPrice, hostBonus);
  return { grant: seq[0]!, afterSpend: seq[1]!, afterHost: seq[2]!, hitsZero: seq[1] === 0 };
}

// ── S9 gamertag inline-check debounce ────────────────────────────────────────

/** The contract bounds (ClaimIdentityRequest gamertag min(2) max(16)). */
export const GAMERTAG_MIN = 2;
export const GAMERTAG_MAX = 16;

/** Normalise as the field renders it — trimmed, upper-cased (S1 code grammar). */
export function normaliseTag(raw: string): string {
  return raw.trim().toUpperCase();
}

/** Only tags inside the contract bounds are worth a network round-trip. */
export function shouldCheckGamertag(raw: string): boolean {
  const t = normaliseTag(raw);
  return t.length >= GAMERTAG_MIN && t.length <= GAMERTAG_MAX;
}

/** The inline dot's state — pulsing grey while checking, then the verdict. */
export type GamertagDot = "idle" | "checking" | "ok" | "taken" | "blocked" | "invalid";

export function gamertagDot(opts: {
  tag: string;
  checking: boolean;
  result: GamertagCheck | null;
}): GamertagDot {
  if (!shouldCheckGamertag(opts.tag)) return "idle";
  if (opts.checking) return "checking";
  if (opts.result === null) return "idle";
  if (opts.result.available) return "ok";
  return opts.result.reason === "ok" ? "ok" : opts.result.reason; // "taken" | "blocked" | "invalid"
}

/** The S9 CTA ("MAKE IT MINE") is armed only with an avatar + an available tag. */
export function canClaimIdentity(opts: {
  avatarId: string | null;
  tag: string;
  result: GamertagCheck | null;
}): boolean {
  if (opts.avatarId === null) return false;
  if (!shouldCheckGamertag(opts.tag)) return false;
  return opts.result !== null && opts.result.available;
}

/** Minimal injectable timer surface (default = the globals; fake in tests). */
export interface DebounceTimers {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

const DEFAULT_TIMERS: DebounceTimers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

export interface Debouncer<A extends unknown[]> {
  /** Schedule fn(...args); a newer call within `ms` supersedes the pending one. */
  call(...args: A): void;
  /** Drop any pending call (unmount / field-clear). */
  cancel(): void;
}

/**
 * Trailing-edge debouncer — the S9 inline check fires once, 400ms after the
 * player stops typing (FIRST BLOOD S1 grammar). Pure over an injectable timer
 * so the coalescing is testable with fake timers.
 */
export function createDebouncer<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number,
  timers: DebounceTimers = DEFAULT_TIMERS,
): Debouncer<A> {
  let handle: unknown = null;
  return {
    call(...args: A) {
      if (handle !== null) timers.clearTimeout(handle);
      handle = timers.setTimeout(() => {
        handle = null;
        fn(...args);
      }, ms);
    },
    cancel() {
      if (handle !== null) {
        timers.clearTimeout(handle);
        handle = null;
      }
    },
  };
}

/** The S9 inline-check debounce window (FIRST BLOOD S1: 400ms). */
export const GAMERTAG_CHECK_DEBOUNCE_MS = 400;
